
import React, { useState } from 'react';
import { DateSelector } from '@/components/admin/sales/DateSelector';
import { SalesMetricsSection } from '@/components/admin/sales/dashboard/SalesMetricsSection';
import { SalesByCarrier } from '@/components/admin/sales/dashboard/SalesByCarrier';
import { SalesByLeadSource } from '@/components/admin/sales/dashboard/SalesByLeadSource';
import { SalesByPolicyLine } from '@/components/admin/sales/dashboard/SalesByPolicyLine';
import { SalesByType } from '@/components/admin/sales/dashboard/SalesByType';
import { EstimatedCommissions } from '@/components/admin/sales/dashboard/EstimatedCommissions';
import { LoadingIndicator } from '@/components/admin/sales/dashboard/LoadingIndicator';
import { useSalesAccess } from '@/hooks/useSalesAccess';
import { usePolicyMetrics, PolicyMetrics } from '@/hooks/usePolicyMetrics';
import { useNavigate } from 'react-router-dom';
import { AccessDenied } from '@/components/admin/sales/dashboard/AccessDenied';
import { SalesDashboardHeader } from '@/components/admin/sales/dashboard/SalesDashboardHeader';
import { SalesMetricsControls } from '@/components/admin/sales/dashboard/SalesMetricsControls';
import AdminLayout from '@/components/layout/AdminLayout';
import { useSalesDialogs } from '@/hooks/useSalesDialogs';

// Update SalesMetricsBasic to match the properties needed by the components
export interface SalesMetricsBasic {
  daily: number;
  weekly: number;
  monthly: number;
  yearly: number;
  endorsementPremium: number;
  dailyTotal: number;
  weeklyTotal: number;
  monthlyTotal: number;
  yearlyTotal: number;
  dailyPolicies: any[];
  weeklyPolicies: any[];
  monthlyPolicies: any[];
  yearlyPolicies: any[];
  endorsementPolicies: any[];
}

export default function SalesDashboard() {
  const navigate = useNavigate();
  const { loading, hasAccess, role } = useSalesAccess();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [appliedDate, setAppliedDate] = useState<Date | undefined>(undefined);
  const { metrics, isLoading, error } = usePolicyMetrics(appliedDate);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState('');

  // Handle "no access" scenario
  if (!loading && !hasAccess) {
    return <AccessDenied />;
  }

  const handleDateChange = (date: Date | undefined) => {
    setSelectedDate(date);
  };

  const handleApplyDate = (date: Date | undefined) => {
    setAppliedDate(date);
  };

  const handleRefreshMetrics = async () => {
    setIsRefreshing(true);
    setRefreshMessage('Refreshing metrics...');
    
    // Just re-fetch with current date
    setSelectedDate(new Date());
    setAppliedDate(new Date());
    
    setTimeout(() => {
      setIsRefreshing(false);
      setRefreshMessage('Metrics refreshed');
      
      // Clear message after 3 seconds
      setTimeout(() => {
        setRefreshMessage('');
      }, 3000);
    }, 1000);
  };

  return (
    <AdminLayout title="Sales Dashboard" showSidebar={false}>
      <SalesDashboardHeader
        title="Sales Metrics"
        role={role}
        isResetting={false}
        resetMessage=""
        selectedDate={selectedDate}
        onDateChange={handleDateChange}
        onApply={handleApplyDate}
        onResetMetrics={() => {}} // Not used anymore
      />

      <SalesMetricsControls 
        isUpdating={isRefreshing}
        updateMessage={refreshMessage}
        onUpdateMetrics={handleRefreshMetrics}
      />

      {loading || isLoading ? (
        <LoadingIndicator />
      ) : (
        <div className="space-y-6">
          <SalesMetricsSection 
            metrics={metrics as SalesMetricsBasic} 
            isHistorical={!!appliedDate} 
          />
          
          <div className="space-y-6 mb-6">
            <SalesByCarrier salesMetrics={metrics as SalesMetricsBasic} />
            <SalesByLeadSource salesMetrics={metrics as SalesMetricsBasic} />
          </div>
          
          <div className="space-y-6 mb-6">
            <SalesByPolicyLine salesMetrics={metrics as SalesMetricsBasic} />
            <SalesByType salesMetrics={metrics as SalesMetricsBasic} />
          </div>
          
          <div className="space-y-6 mb-6">
            <EstimatedCommissions salesMetrics={metrics as SalesMetricsBasic} />
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
